import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate, useParams } from "react-router-dom";
import { Accordion, AccordionTab } from "primereact/accordion";

import PageLayout from "../PageLayout";
import Btn from "../../ui/Btn";

import appStore from "../../storage/AppStore";
import clubStore from "../../storage/ClubStore";
import systemStore from "../../storage/SystemStore";

import "../../common/game.css";

const safeArray = (v) => (Array.isArray(v) ? v : []);

const getMemberId = (m) => m?.company_user_id || m?.id || null;
const getMemberTitle = (m) => m?.title || "-";
const getMemberBillId = (m) => m?.bill_id || null;

const isMemberBilled = (m) => !!m?.is_billed;
const isMemberPaid = (m) => !!m?.is_paid;
const getMemberAmount = (m) => m?.amount || 0;

const CompetitionBilling = ({ role = "MGR" }) => {

  const navigate = useNavigate();
  const { id } = useParams();

  const basePath = role === "TRN" ? "/trainer" : "/manager";

  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedByTeam, setSelectedByTeam] = useState({});
  const [loadingGenerateByTeam, setLoadingGenerateByTeam] = useState({});
  const [loadingInformByMember, setLoadingInformByMember] = useState({});

  useEffect(() => {
    clubStore.tournament.getCompetitionBilling(id).catch(console.error);
  }, [id]);

  const tournament = clubStore.tournament.competition || null;

  const teams = useMemo(() => safeArray(tournament?.teams), [tournament]);

  // ✅ smart default: only unbilled
  useEffect(() => {
    if (!teams.length) return;

    const next = {};

    teams.forEach((team) => {
      const members = safeArray(team?.members);

      next[team.id] = members
        .filter((m) => !isMemberBilled(m))
        .map((m) => getMemberId(m))
        .filter(Boolean);
    });

    setSelectedByTeam(next);

  }, [teams]);

  const getTeamMembers = (team) => safeArray(team?.members);

  const getSelectedIds = (teamId) => selectedByTeam[teamId] || [];

  const setSelectedIds = (teamId, memberIds) => {
    setSelectedByTeam((prev) => ({
      ...prev,
      [teamId]: memberIds,
    }));
  };

  const toggleMember = (teamId, member) => {

    const memberId = getMemberId(member);
    if (!memberId) return;

    const selectedIds = getSelectedIds(teamId);

    if (selectedIds.includes(memberId)) {
      setSelectedIds(teamId, selectedIds.filter((x) => x !== memberId));
    } else {
      setSelectedIds(teamId, [...selectedIds, memberId]);
    }

  };

  // ✅ only unbilled selectable
  const toggleAll = (team) => {

    const members = getTeamMembers(team);

    const selectableIds = members
      .filter((m) => !isMemberBilled(m))
      .map((m) => getMemberId(m))
      .filter(Boolean);

    const selectedIds = getSelectedIds(team.id);

    const isAllSelected =
      selectableIds.length > 0 &&
      selectableIds.every((id) => selectedIds.includes(id));

    if (isAllSelected) {
      setSelectedIds(team.id, []);
    } else {
      setSelectedIds(team.id, selectableIds);
    }

  };

  const refreshTournament = async () => {
    await clubStore.tournament.getCompetitionBilling(id);
  };

  const handleGenerateBills = async (team) => {

    const selectedIds = getSelectedIds(team.id);

    if (!selectedIds.length) {
      systemStore.setError(appStore.tr.select_members || "Select members first");
      return;
    }

    try {

      setLoadingGenerateByTeam((prev) => ({ ...prev, [team.id]: true }));

      const ret = await clubStore.tournament.generateCompetitionBilling({
        tournament_id: Number(id),
        tournament_team_id: team.id,
        company_user_ids: selectedIds,
      });

      if (ret?.result === 0) {
        systemStore.setSuccess(appStore.tr.billing_generated || "Billing generated");
        await refreshTournament();
      }

    } catch (err) {
      systemStore.setError(err?.message || "Failed to generate billing");
    } finally {
      setLoadingGenerateByTeam((prev) => ({ ...prev, [team.id]: false }));
    }

  };

  const handleInformBill = async (team, member) => {

    const memberId = getMemberId(member);
    const billId = getMemberBillId(member);
    const key = `${team.id}_${memberId}`;

    try {

      setLoadingInformByMember((prev) => ({ ...prev, [key]: true }));

      const ret = await clubStore.tournament.informCompetitionBill({
        tournament_id: Number(id),
        tournament_team_id: team.id,
        company_user_id: memberId,
        bill_id: billId,
      });

      if (ret?.result === 0) {
        systemStore.setSuccess(appStore.tr.bill_informed || "Bill informed");
      }

    } catch (err) {
      systemStore.setError(err?.message || "Failed to inform");
    } finally {
      setLoadingInformByMember((prev) => ({ ...prev, [key]: false }));
    }

  };

  const handleDeleteBill = async (team, member) => {

    if (!window.confirm(appStore.tr.are_you_sure_delete || "Are you sure to delete?")) {
      return;
    }

    const memberId = getMemberId(member);
    const billId = getMemberBillId(member);

    try {

      const ret = await clubStore.tournament.deleteCompetitionBilling({
        tournament_id: Number(id),
        tournament_team_id: team.id,
        company_user_id: memberId,
        bill_id: billId,
      });

      if (ret?.result === 0) {
        systemStore.setSuccess(appStore.tr.billing_deleted || "Deleted");
        await refreshTournament();
      }

    } catch (err) {
      systemStore.setError(err?.message || "Delete failed");
    }

  };

  const renderTeamHeader = (team) => {
    const members = getTeamMembers(team);
    const billedCount = members.filter(isMemberBilled).length;
    const selectedCount = getSelectedIds(team.id).length;
    const debtAmount = members
      .filter((m) => isMemberBilled(m) && !isMemberPaid(m))
      .reduce((sum, m) => sum + getMemberAmount(m), 0);

    return (
      <div className="w-full min-w-0 pr-2">
        <div className="flex align-items-center flex-wrap gap-2 mb-2">
          <i className="pi pi-users text-primary" aria-hidden="true" />
          <span className="font-semibold flex-1 min-w-0">{team.name || "-"}</span>
          <span className="text-600 text-sm">
            {appStore.tr.players}: {members.length}
          </span>
        </div>
        <div className="game-badges">
          <span className="badge badge-upcoming">
            <i className="pi pi-check-square" aria-hidden="true" />
            {appStore.tr.selected}: {selectedCount}
          </span>
          <span className="badge badge-tournament">
            <i className="pi pi-credit-card" aria-hidden="true" />
            {appStore.tr.billed}: {billedCount}
          </span>
          <span className="badge badge-danger">
            <i className="pi pi-wallet" aria-hidden="true" />
            {appStore.tr.total_debt || "Debt"}: {debtAmount}
          </span>
        </div>
      </div>
    );
  };

  const renderMemberRow = (team, member, index) => {
    const memberId = getMemberId(member) || index;
    const billed = isMemberBilled(member);
    const isPaid = isMemberPaid(member);
    const amount = getMemberAmount(member);
    const selectedIds = getSelectedIds(team.id);
    const checked = isPaid || selectedIds.includes(memberId);

    return (
      <div key={memberId} className="col-12 lg:col-6">
        <div className="game-card h-full flex flex-column justify-content-between">
          <div>
            <div className="flex align-items-start gap-3">
              <span className="badge badge-upcoming flex-shrink-0">
                <i className="pi pi-user" aria-hidden="true" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold line-height-3">{getMemberTitle(member)}</div>
                {billed && (
                  <div className="text-sm text-600 mt-2">
                    <i className="pi pi-wallet mr-2" aria-hidden="true" />
                    <b>{amount}</b>
                  </div>
                )}
              </div>
              <input
                type="checkbox"
                className="flex-shrink-0"
                aria-label={`${appStore.tr.selected || "Select"}: ${getMemberTitle(member)}`}
                checked={checked}
                disabled={billed || isPaid}
                onChange={() => toggleMember(team.id, member)}
              />
            </div>

            <div className="game-badges mt-3">
              <span className={`badge ${billed ? "badge-tournament" : "badge-upcoming"}`}>
                <i className={`pi ${billed ? "pi-credit-card" : "pi-clock"}`} aria-hidden="true" />
                {billed ? appStore.tr.billed : appStore.tr.not_billed}
              </span>
              {billed && (
                <span className={`badge ${isPaid ? "badge-success" : "badge-warning"}`}>
                  <i className={`pi ${isPaid ? "pi-check-circle" : "pi-exclamation-circle"}`} aria-hidden="true" />
                  {isPaid ? appStore.tr.paid : appStore.tr.unpaid}
                </span>
              )}
            </div>
          </div>

          {(billed && (role === "MGR" || !isPaid)) && (
            <div className="game-card-actions justify-content-end mt-3 pt-3 border-top-1 surface-border">
              {role === "MGR" && (
                <Btn
                  icon="pi pi-eye"
                  severity="success"
                  outlined
                  tooltip={appStore.tr.view || "View"}
                  onClick={() => navigate(`/manager/billing/detail/${member.bill_id}`)}
                />
              )}
              {!isPaid && (
                <>
                  <Btn
                    icon="pi pi-bell"
                    tooltip={appStore.tr.inform}
                    loading={!!loadingInformByMember[`${team.id}_${getMemberId(member)}`]}
                    onClick={() => handleInformBill(team, member)}
                    disabled={!billed}
                  />
                  <Btn
                    icon="pi pi-trash"
                    severity="danger"
                    tooltip={appStore.tr.delete}
                    onClick={() => handleDeleteBill(team, member)}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderTeamContent = (team) => {
    const members = getTeamMembers(team);
    const selectedIds = getSelectedIds(team.id);
    const selectableIds = members
      .filter((m) => !isMemberBilled(m))
      .map((m) => getMemberId(m))
      .filter(Boolean);

    const isAllSelected =
      selectableIds.length > 0 &&
      selectableIds.every((id) => selectedIds.includes(id));

    return (
      <div className="w-full min-w-0">
        <div className="competition-detail-item mb-3">
          <div className="flex align-items-center justify-content-between flex-wrap gap-3">
            <div className="flex align-items-center gap-2 text-600 text-sm">
              <i className="pi pi-users" aria-hidden="true" />
              {appStore.tr.players}: <b>{members.length}</b>
            </div>
            <div className="flex flex-wrap gap-2">
              <Btn
                icon={isAllSelected ? "pi pi-stop" : "pi pi-check-square"}
                label={isAllSelected
                  ? (appStore.tr.unselect_all || "Unselect all")
                  : (appStore.tr.select_all || "Select all")}
                onClick={() => toggleAll(team)}
              />
              <Btn
                icon="pi pi-dollar"
                label={appStore.tr.add_billing}
                onClick={() => handleGenerateBills(team)}
                loading={!!loadingGenerateByTeam[team.id]}
                disabled={!selectedIds.length}
              />
            </div>
          </div>
        </div>

        {members.length ? (
          <div className="grid">
            {members.map((member, index) => renderMemberRow(team, member, index))}
          </div>
        ) : (
          <div className="game-page p-4 text-center text-600">
            <i className="pi pi-users text-xl mb-2" aria-hidden="true" />
            <div>{appStore.tr.no_members || "No members"}</div>
          </div>
        )}
      </div>
    );
  };

  return (
    <PageLayout
      header={
        <div className="flex align-items-center gap-3 w-full min-w-0">
          <i className="pi pi-wallet text-primary text-xl" aria-hidden="true" />
          <div className="flex flex-column gap-1 min-w-0">
            <h3 className="m-0">{appStore.tr.tournament_billing}</h3>
            <span className="text-600 text-sm">{tournament?.name}</span>
          </div>
        </div>
      }
    >
      <div className="col-12">
        {teams.length ? (
          <Accordion
            activeIndex={activeIndex}
            onTabChange={(e) => setActiveIndex(e.index)}
          >
            {teams.map((team) => (
              <AccordionTab key={team.id} header={renderTeamHeader(team)}>
                {renderTeamContent(team)}
              </AccordionTab>
            ))}
          </Accordion>
        ) : (
          <div className="game-page p-4 text-center text-600">
            <i className="pi pi-users text-xl mb-2" aria-hidden="true" />
            <div>{appStore.tr.no_competition_teams || "No competition teams"}</div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default observer(CompetitionBilling);
